import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react'
import { get } from 'lodash'
import { searchBets } from '../../api/bettor'
import { searchActiveCashoutOrders } from '../../api/order'
import { useApplicationState } from '../../state'
import {
  BET_STATUS_ALL,
  BET_STATUS_SETTLED,
  BET_STATUS_TO_SELL,
  compositeBetStatuses,
  getDayRange,
  getTwoDayRange,
  last24,
  last48,
  SET_BET,
  SET_BETS,
  SET_BETS_BY_REQUEST_TIME,
  SET_ORDER_CASHOUT_BETS,
  UPDATE_STATE
} from './constants'
import { betHistoryReducer, getInitialState } from './betHistoryReducer'
import bus from '../../bus'
import events from '../../bus/events'
import { insertInArrayByIndex, logger } from '../../utils'
import { BET_STATUSES } from '../../constants'
import { SiteConfigManager } from '../../index'

const betHistory = createContext()

export const BetHistoryProvider = betHistory.Provider

export const useBetHistory = () => (
  useContext(betHistory)
)

const {
  UNSETTLED,
  LOSE,
  WIN,
  VOID,
  CASH_OUT,
  CANCELLED,
  PENDING_APPROVAL
} = BET_STATUSES

export const createBetHistoryState = (initState) => {
  const applicationState = useApplicationState()
  const authenticated = get(applicationState, 'authenticated')
  const initialState = useMemo(() => getInitialState(initState || {}), [])
  const [state, dispatch] = useReducer(betHistoryReducer, initialState)
  const {
    selectedBetStatus,
    activeTimeFilter,
    bets,
    orderCashoutBets,
    currentPage,
    itemsPerPage,
    range
  } = state

  const setBets = useCallback(bets => {
    dispatch({
      type: SET_BETS,
      payload: {
        bets
      }
    })
  }, [])

  const setOrderCashoutBets = useCallback(orderCashoutBets => dispatch({
    type: SET_ORDER_CASHOUT_BETS,
    payload: {
      orderCashoutBets
    }
  }), [])

  const setCurrentPage = useCallback(currentPage => dispatch({
    type: UPDATE_STATE,
    payload: {
      currentPage
    }
  }), [])

  const setActiveTimeFilter = useCallback((activeTimeFilter) => {
    dispatch({
      type: UPDATE_STATE,
      payload: {
        activeTimeFilter,
        currentPage: 0
      }
    })
  }, [])

  const setRange = useCallback((range) => {
    dispatch({
      type: UPDATE_STATE,
      payload: {
        range,
        currentPage: 0
      }
    })
  }, [])

  const onSelectBetStatus = useCallback((betStatus) => {
    if (selectedBetStatus === betStatus) return

    dispatch({
      type: UPDATE_STATE,
      payload: {
        selectedBetStatus: betStatus,
        currentPage: 0
      }
    })
  }, [selectedBetStatus])

  const replaceBet = useCallback((newBet, prevBetId) => {
    const indexOfBet = Object.values(bets).findIndex(
      (bet) => bet.betId === prevBetId
    )

    if (indexOfBet === -1) return

    setBets(
      insertInArrayByIndex(
        bets,
        indexOfBet,
        newBet
      )
    )
  }, [bets])

  const setOrderCashoutInfoToBet = useCallback((orderCashoutInfo, betId) => {
    const betIndex = Object.values(orderCashoutBets).findIndex(
      (bet) => bet.betId === betId
    )

    let newBets

    if (betIndex === -1) {
      newBets = [
        ...orderCashoutBets,
        orderCashoutInfo
      ]
    } else {
      newBets = insertInArrayByIndex(
        orderCashoutBets,
        betIndex,
        orderCashoutInfo
      )
    }

    setOrderCashoutBets(newBets)
  }, [orderCashoutBets])

  useEffect(() => {
    if (!authenticated) {
      setBets([])
      return
    }

    const ac = new AbortController()

    const isHideRejected = SiteConfigManager.getConfig(
      'config.config.bet_history.hide_rejected',
      false
    )

    let betStatuses
    if (selectedBetStatus === BET_STATUS_ALL) {
      // if user deselect all filter -> all filters should be displayed
      betStatuses = compositeBetStatuses[BET_STATUS_ALL]
    } else if (selectedBetStatus === BET_STATUS_TO_SELL) {
      //  special case. we fetch all unsettled bets and then filter OUT all bets WITHOUT cashout
      betStatuses = compositeBetStatuses[BET_STATUS_TO_SELL]
    } else if (selectedBetStatus === BET_STATUS_SETTLED) {
      betStatuses = compositeBetStatuses[BET_STATUS_SETTLED]
    } else {
      betStatuses = [selectedBetStatus]
    }

    if (isHideRejected) {
      betStatuses = betStatuses.filter(status => status !== BET_STATUSES.REJECTED)
    }

    const lastRequestTime = new Date()
    let createDate = range

    if (activeTimeFilter === last24) {
      createDate = getDayRange()
    } else if (activeTimeFilter === last48) {
      createDate = getTwoDayRange()
    }

    dispatch({
      type: UPDATE_STATE,
      payload: {
        isLoading: true,
        isError: false,
        lastRequestTime
      }
    })

    searchBets({
      signal: ac.signal,
      body: {
        filter: {
          betStatuses,
          createDate
        },
        sorting: [
          {
            direction: 'DESC',
            fieldName: 'createTime',
            order: 0
          }
        ],
        paging: {
          currentPage,
          itemsPerPage
        }
      }
    })
      .then(({ bets, paging }) => {
        dispatch({
          type: SET_BETS_BY_REQUEST_TIME,
          payload: {
            bets,
            isLoading: false,
            totalPages: paging.totalPages,
            lastRequestTime
          }
        })
      })
      .catch(e => {
        logger.error('[bet:history]', 'unable to load bets', e)

        dispatch({
          type: SET_BETS_BY_REQUEST_TIME,
          payload: {
            bets: [],
            isError: true,
            isLoading: false,
            lastRequestTime
          }
        })
      })
    return () => ac.abort()
  }, [authenticated, selectedBetStatus, activeTimeFilter, currentPage, range])

  useEffect(() => {
    if (!authenticated) {
      setOrderCashoutBets([])
      return
    }

    const ac = new AbortController()

    searchActiveCashoutOrders({
      data: {
        filter: {
          orderCashOutStatus: 'ACTIVE'
        }
      },
      signal: ac.signal
    })
      .then(({ data }) => {
        setOrderCashoutBets(data)
      })
      .catch(e => {
        logger.error('[bet:history]', 'unable to load active cashout orders', e)

        setOrderCashoutBets([])
      })

    const handleBetUpdated = ({ data }) => {
      dispatch({
        type: SET_BET,
        payload: data
      })
    }

    bus.on(events.bet.updated, handleBetUpdated)

    return () => {
      ac.abort()
      bus.off(events.bet.updated, handleBetUpdated)
    }
  }, [authenticated])

  return {
    ...state,
    setActiveTimeFilter,
    setBets,
    setOrderCashoutBets,
    onSelectBetStatus,
    replaceBet,
    setOrderCashoutInfoToBet,
    setRange,
    setCurrentPage
  }
}
